/**
 * Customer 360 search — /agent/customers
 *
 * Find a customer by name, email or phone, then open their consolidated
 * cross-channel profile. Data + filters come from the same `useClients` hook
 * and toolbar as the Clients list (/admin/clients, clients:view), so status,
 * group, sort and date range behave identically; this page only swaps the
 * row action for "Open 360" and adds customer creation.
 */
import { useEffect, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Contact, Eye, Loader2, Lock, Pencil, Phone, Plus, RefreshCw, ScanSearch, Search, X } from 'lucide-react'
import { useClients } from '../../../hooks/useClients.js'
import { useClientGroups } from '../../../hooks/useClientGroups.js'
import {
  ActionButton, getAvatarColor, getInitials, groupPill, isSystemName, StatusBadge,
} from '../../../components/agent/clients/ClientsTable.js'
import ClientsPaginationBar from '../../../components/agent/clients/ClientsPaginationBar.js'
import ClientsFilterToolbar from '../../../components/agent/clients/ClientsFilterToolbar.js'
import CreateClientPanel from '../../../components/agent/clients/CreateClientPanel.js'
import ClientViewDrawer from '../../../components/agent/clients/ClientViewDrawer.js'
import { formatDate } from '../../../utils/formatDate.js'

/* ── Design tokens: radius capped at 3px across the whole surface ── */
const R = 'rounded-[3px]'
const ICON_BTN = `flex h-9 w-9 shrink-0 items-center justify-center ${R} border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900`

const COLUMNS = ['Customer', 'Phone Number', 'Group / Channel', 'Status', 'Created']

export default function CustomersSearchPage(): JSX.Element {
  const navigate = useNavigate()
  const { groups } = useClientGroups()
  const clientsState = useClients()
  const {
    clients, total, loading, search, setSearch, page, pageSize, setPage, setPageSize, refetch,
  } = clientsState

  // Typed locally, committed on Enter — same as the Clients list.
  const [searchInput, setSearchInput] = useState(search)
  useEffect(() => { setSearchInput(search) }, [search])

  // Create / edit share one panel; `null` = closed.
  const [panel, setPanel] = useState<{ mode: 'create' | 'edit'; clientId?: string } | null>(null)
  const [viewId, setViewId] = useState<string | null>(null)

  const groupName = (id: string): string => groups.find((g) => g.id === id)?.name ?? 'No Group'
  const openCustomer = (id: string): void => { void navigate(`/agent/customers/${id}`) }

  return (
    <div className="flex h-full flex-col bg-white">
      {/* ── Header: identity · search · actions ── */}
      <header className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-slate-200 bg-white px-6 py-3.5">
        <div className="flex shrink-0 items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center border border-blue-200 ${R} bg-blue-50 text-[#3366FF]`}>
            <ScanSearch className="h-5 w-5" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold leading-tight tracking-tight text-slate-900">Customer 360</h1>
              <span className={`${R} border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600`}>Total: <span className="font-mono">{total.toLocaleString()}</span></span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">Search a customer to see every channel, ticket and call in one view</p>
          </div>
        </div>

        <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-2.5">
          <div className="relative w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setSearch(searchInput.trim()) } }}
              className={`w-full py-1.5 pl-9 pr-8 text-xs ${R} border border-slate-300 bg-slate-50 text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#3366FF] focus:bg-white focus:ring-1 focus:ring-[#3366FF]`}
            />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setSearch('') }} className={`absolute right-1.5 top-1/2 -translate-y-1/2 ${R} p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`} title="Clear">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <button onClick={() => void refetch()} className={ICON_BTN} title="Refresh customer list">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setPanel({ mode: 'create' })}
            className={`flex shrink-0 items-center gap-1.5 ${R} bg-[#3366FF] px-3.5 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-[#2952CC]`}
          >
            <Plus className="h-4 w-4" />New Customer
          </button>
        </div>
      </header>

      {/* ── Toolbar — shared with the Clients list ── */}
      <ClientsFilterToolbar clients={clientsState} groups={groups} />

      {/* ── Results table ── */}
      <div className="scrollbar-thin relative flex-1 overflow-auto bg-white">
        {loading && clients.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading customers…
          </div>
        ) : clients.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 py-20">
            <div className={`mb-5 flex h-14 w-14 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
              <Contact className="h-6 w-6" />
            </div>
            <h3 className="mb-1.5 text-[15px] font-bold text-slate-900">No customers</h3>
            <p className="mb-6 max-w-sm text-center text-[13px] leading-relaxed text-slate-400">
              {search ? `No customers match “${search}”. Try a different term or clear your filters.` : 'Nothing matches these filters right now.'}
            </p>
            <button onClick={() => setPanel({ mode: 'create' })} className={`flex h-9 items-center gap-2 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}>
              <Plus className="h-4 w-4" />New customer
            </button>
          </div>
        ) : (
          <>
            {loading && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                <Loader2 className="h-6 w-6 animate-spin text-[#3366FF]" />
              </div>
            )}
            <table className="w-full border-collapse text-left text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="select-none border-b border-slate-200 bg-slate-50">
                  {COLUMNS.map((h) => (
                    <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                  <th className="w-[280px] px-4 py-3 pr-6 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Quick Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {clients.map((c) => {
                  const g = groupName(c.group_id)
                  const pill = groupPill(g)
                  const mono = isSystemName(c.full_name)
                  return (
                    <tr
                      key={c.id}
                      onClick={() => openCustomer(c.id)}
                      className="group cursor-pointer transition-colors hover:bg-slate-50/70"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`flex h-8 w-8 shrink-0 items-center justify-center ${R} text-xs font-bold ${getAvatarColor(c.email)}`}>
                            {getInitials(c.full_name, c.email)}
                          </div>
                          <div className="min-w-0">
                            <span className={`block truncate text-[13px] font-semibold text-slate-900 transition-colors group-hover:text-[#3366FF] ${mono ? 'font-mono' : ''}`}>
                              {c.full_name || '—'}
                              {c.locked && <Lock className="ml-1 inline h-3 w-3 text-amber-500" />}
                            </span>
                            <p className={`truncate text-[11px] text-slate-500 ${mono ? 'font-mono' : ''}`}>{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {c.phone_number ? (
                          <span className="inline-flex items-center gap-1.5 font-mono text-slate-600">
                            <Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone_number}
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 border ${R} px-2 py-0.5 text-[11px] font-medium ${pill.cls}`}>
                          <pill.Icon className="h-3 w-3" />{g}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(c.createdAt)}</td>
                      <td className="whitespace-nowrap px-4 py-3 pr-6 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <ActionButton onClick={() => setViewId(c.id)} Icon={Eye} label="View" hover="view" />
                          <ActionButton onClick={() => setPanel({ mode: 'edit', clientId: c.id })} Icon={Pencil} label="Edit" hover="edit" />
                          <ActionButton onClick={() => openCustomer(c.id)} Icon={ScanSearch} label="Open 360" hover="open" />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ── Footer pager — shared with the Clients list ── */}
      {total > 0 && (
        <ClientsPaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          noun="customers"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      <CreateClientPanel
        open={panel !== null}
        mode={panel?.mode ?? 'create'}
        {...(panel?.clientId ? { clientId: panel.clientId } : {})}
        noun="Customer"
        onClose={() => setPanel(null)}
        onCreated={() => { setPanel(null); void refetch() }}
      />

      {viewId && (
        <ClientViewDrawer open clientId={viewId} onClose={() => setViewId(null)} />
      )}
    </div>
  )
}
